/**
 * Static page media overrides — resolution helpers
 * ------------------------------------------------
 * The portal's Static Page (CMS) tab stores, per page and per block, which
 * image/video should render. This module fetches that map and works out which
 * override applies to a given asset URL on a given route.
 *
 * `matchKeyFor` MUST stay byte-identical to:
 *   - frontend/scripts/scan-page-media.mjs  -> matchKeyFor()
 *   - api/manage_page_media.php             -> cms_match_key()
 */

const API_BASE = import.meta.env.VITE_API_BASE || '/api'
const EMPTY_BUCKET = { replace: {}, hide: [], add: [] }

const isRemote = (u) => /^(https?:)?\/\//i.test(String(u).trim())

export function matchKeyFor(url) {
  let u = String(url || '').trim()
  if (!u) return ''
  if (isRemote(u)) return u.replace(/^https?:/i, '').toLowerCase()
  u = u.split(/[?#]/)[0]
  let base = u.split('/').pop() || u
  try { base = decodeURIComponent(base) } catch { /* keep raw */ }
  base = base.toLowerCase()
  // Vite appends a content hash on bundled assets — strip it so the build and
  // the dev server resolve to the same key.
  return base.replace(/-[a-z0-9_-]{8}(\.[a-z0-9]+)$/i, '$1')
}

const isExactRoute = (pattern, pathname) =>
  pattern.replace(/\/+$/, '') === pathname.replace(/\/+$/, '')

/** Does a stored page_route pattern match the live pathname? */
function routeMatches(pattern, pathname) {
  if (pattern === '*') return true
  if (isExactRoute(pattern, pathname)) return true
  const a = pattern.replace(/\/+$/, '').split('/')
  const b = pathname.replace(/\/+$/, '').split('/')
  if (a.length !== b.length) return false
  return a.every((seg, i) => seg.startsWith(':') || seg === b[i])
}

/* --------------------------------------------------------------- fetching */

let cache = null
let inflight = null

export function clearOverrideCache() {
  cache = null
  inflight = null
}

export function getCachedOverrides() {
  return cache
}

export async function loadOverrides({ force = false } = {}) {
  if (cache && !force) return cache
  if (inflight && !force) return inflight

  inflight = (async () => {
    try {
      const res = await fetch(`${API_BASE}/manage_page_media.php?action=overrides`, {
        headers: { Accept: 'application/json' },
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      cache = data && data.success ? { version: data.version, routes: data.routes || {} } : { version: '0', routes: {} }
    } catch {
      // The CMS is optional decoration — a failure must never break a page.
      cache = { version: '0', routes: {} }
    }
    inflight = null
    return cache
  })()

  return inflight
}

/**
 * Merge the global ("*") bucket with every route bucket matching `pathname`
 * into one flat set of instructions.
 */
export function bucketForRoute(overrides, pathname) {
  const routes = (overrides && overrides.routes) || null
  if (!routes) return EMPTY_BUCKET

  const replace = {}
  const hide = new Set()
  const add = []

  // Route precedence: when a page has its own bucket, a sibling `:param`
  // pattern must not also apply — /services/:slug is a different page from
  // /services/website-development even though the shapes match.
  const patterns = Object.keys(routes)
  const exact = patterns.some((p) => p !== '*' && isExactRoute(p, pathname))

  for (const [pattern, bucket] of Object.entries(routes)) {
    if (exact && pattern !== '*' && !isExactRoute(pattern, pathname)) continue
    if (!routeMatches(pattern, pathname)) continue
    Object.assign(replace, bucket.replace || {})
    for (const key of bucket.hide || []) hide.add(key)
    for (const item of bucket.add || []) add.push(item)
  }

  return { replace, hide: [...hide], add }
}

export function hasWork(bucket) {
  return Boolean(
    bucket && (Object.keys(bucket.replace || {}).length || (bucket.hide || []).length || (bucket.add || []).length)
  )
}
